import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { GraduationCap } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use useRef for processed flag - prevents race conditions under StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL hash
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        toast.error("Authentication failed");
        navigate("/login");
        return;
      }

      try {
        // Exchange session_id for a JWT + session cookie
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        const { access_token, user: userData } = response.data;
        // Clear the session_id fragment from the URL
        window.history.replaceState(null, "", window.location.pathname);
        login(userData, access_token);

        toast.success("Welcome to TheMatheMind 360!");

        const stashedRedirect = sessionStorage.getItem("post_login_redirect");
        if (stashedRedirect) {
          sessionStorage.removeItem("post_login_redirect");
          navigate(stashedRedirect, { replace: true });
        } else if (userData.role === "admin") {
          navigate("/admin", { replace: true });
        } else if (userData.role === "teacher") {
          navigate("/teacher", { replace: true });
        } else if (userData.role === "player") {
          navigate("/play/arena", { replace: true });
        } else {
          navigate("/student", { replace: true });
        }
      } catch (error) {
        console.error("Auth callback error:", error);
        toast.error("Authentication failed. Please try again.");
        navigate("/login", { replace: true });
      }
    };

    processAuth();
  }, [login, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center animate-pulse">
          <GraduationCap className="w-10 h-10 text-primary-foreground" />
        </div>
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        <p className="text-muted-foreground font-medium">Completing sign in...</p>
      </div>
    </div>
  );
}
